export type ClaudeSkillSecurityFaqItem = {
  question: string;
  answer: string;
};

export type ClaudeSkillSecurityChecklistItem = {
  question: string;
  body: string;
};

export type ClaudeSkillSecurityReportFinding = {
  severity: string;
  severityKey: "critical" | "high" | "medium" | "low";
  title: string;
  detail: string;
  evidence: string;
  confidence: string;
  location: string;
};

export type ClaudeSkillSecurityCopy = {
  metadata: {
    title: string;
    description: string;
    canonicalUrl: string;
    h1: string;
    robots: string;
  };
  navigation: {
    ariaLabel: string;
    whyReview: string;
    checklist: string;
    coverage: string;
    report: string;
    faq: string;
  };
  hero: {
    purpose: string;
    visualLines: readonly [string, string];
  };
  scanner: {
    title: string;
    subtitle: string;
    ariaLabel: string;
    inputLabel: string;
    placeholder: string;
    uploadLabel: string;
    uploadHint: string;
    scanActionLabel: string;
    staticOnlyLabel: string;
    staticReadOnlyBody: string;
  };
  whyReview: {
    heading: string;
    introductionBeforeTools: string;
    introductionTools: string;
    introductionAfterTools: string;
    risks: readonly string[];
    evidenceBeforeStudy: string;
    evidenceStudy: string;
    evidenceAfterStudy: string;
  };
  checklist: {
    heading: string;
    introduction: string;
    items: readonly ClaudeSkillSecurityChecklistItem[];
  };
  coverage: {
    heading: string;
    introduction: string;
    detectedLabel: string;
    detected: readonly string[];
    notCoveredLabel: string;
    notCovered: readonly string[];
  };
  report: {
    heading: string;
    summary: string;
    fixture: {
      ariaLabel: string;
      headerLabel: string;
      identifier: string;
      score: number;
      scoreLabel: string;
      decisionTitle: string;
      decisionBody: string;
      recommendationLabel: string;
      recommendationTitle: string;
      recommendationBody: string;
      summary: readonly { label: string; value: string }[];
      coverageTitle: string;
      coverageBody: string;
      findingsLabel: string;
      findings: readonly ClaudeSkillSecurityReportFinding[];
      exportAction: string;
      exportHint: string;
      shareAction: string;
      shareHint: string;
    };
  };
  faq: {
    heading: string;
    items: readonly ClaudeSkillSecurityFaqItem[];
    action: string;
  };
  sources: {
    anthropicSkills: string;
    snykToxicSkills: string;
    csaSkillSupplyChain: string;
    owaspAgenticSkills: string;
  };
};

export const claudeSkillSecurityScannerCopy = {
  title: "Scan a Skill",
  subtitle: "Add a SKILL.md source to begin.",
  ariaLabel: "Claude Skill scanner",
  inputLabel: "Skill Source",
  placeholder:
    "Paste SKILL.md content, enter a GitHub URL like https://github.com/org/repo/blob/main/SKILL.md, or attach a file…",
  uploadLabel: "Upload",
  uploadHint: ".md or .zip",
  scanActionLabel: "Scan Now",
  staticOnlyLabel: "Reads source as plain text:",
  staticReadOnlyBody: "nothing is executed.",
} as const satisfies ClaudeSkillSecurityCopy["scanner"];

/**
 * Page-owned English content for /claude-skill-security.
 *
 * The visible prose follows the approved product spec. Visible em and en dashes are replaced with
 * ordinary punctuation only; the approved SEO metadata remains exact.
 */
export const claudeSkillSecurityCopy = {
  metadata: {
    title: "Claude Skill Security Review & Free Scanner | SkillSecurity",
    description:
      "Scan a Claude Code SKILL.md before installing. Free static analysis checks for prompt injection, data exfiltration, risky permissions, and supply-chain threats.",
    canonicalUrl: "https://skillsecurity.net/claude-skill-security",
    h1: "Claude Skill Security Review",
    robots: "index,follow",
  },
  navigation: {
    ariaLabel: "Claude Skill security review",
    whyReview: "Why review",
    checklist: "Review checklist",
    coverage: "Coverage",
    report: "Report",
    faq: "FAQ",
  },
  hero: {
    purpose: "Scan and review a Claude Code Skill before installing it.",
    visualLines: ["CLAUDE SKILL", "SECURITY REVIEW"],
  },
  scanner: claudeSkillSecurityScannerCopy,
  whyReview: {
    heading: "Why review a Claude Skill before installing",
    introductionBeforeTools: "Claude Skills and ",
    introductionTools: "Claude Code Skills",
    introductionAfterTools:
      " are not passive plugins. A Skill contains natural-language instructions that influence how Claude behaves, and Claude may already have access to files, shell commands, network calls, and environment variables, depending on the tools and permissions approved for that session. A malicious or poorly written Skill can induce Claude to:",
    risks: [
      "read sensitive files (SSH keys, .env, API credentials);",
      "send data to external servers;",
      "run destructive commands;",
      "bypass safety instructions or misuse tools already approved for the session.",
    ],
    evidenceBeforeStudy:
      "Public registry data, not a Claude-only sample, illustrates the broader Agent Skill supply-chain risk. In a ",
    evidenceStudy: "February 2026 study of 3,984 Skills collected from ClawHub and skills.sh",
    evidenceAfterStudy:
      ", Snyk reported that 534 Skills (13.4%) triggered at least one critical-severity security policy. Researchers separately confirmed 76 Skills containing malicious payloads. These figures reflect Snyk's dataset and methodology, not the prevalence of malicious Claude Skills.",
  },
  checklist: {
    heading: "What to review in a SKILL.md before trusting a Skill",
    introduction:
      "Use this checklist with the scan results to inspect the Skill before installing, whether or not the scanner flags anything.",
    items: [
      {
        question: "What does this Skill ask Claude to do?",
        body: "Read the model-instruction section of the Skill. Look for instructions that change Claude's default behavior, especially instructions to ignore safety guidelines, suppress disclaimers, or treat the Skill's author as inherently trusted.",
      },
      {
        question: "What tools would this Skill need, and what does it actually request?",
        body: "If the Skill or its associated configuration declares tool permissions, check which tools are listed. A Skill that only provides code-review advice should not need Bash or WebFetch. Be cautious of broad permissions like unrestricted shell access.",
      },
      {
        question: "Does the Skill reference external scripts or dependencies?",
        body: "Check whether the Skill references executable scripts, unpinned package versions, or install commands. A script that runs curl | bash or pulls dependencies from an unverified source is a supply-chain risk.",
      },
      {
        question: "Does the Skill send data anywhere?",
        body: "Search for URLs, webhooks, or API endpoints in the Skill content and any bundled scripts. A data-exfiltration pattern often looks like reading a credential file and POSTing it to an external server.",
      },
      {
        question: "What files and environment variables could the Skill reach?",
        body: "Claude may have access to your working directory and environment. Check whether the Skill references file paths like ~/.ssh, .env, ~/.aws/credentials, or system keychains, especially in combination with network access.",
      },
      {
        question: "Who published this Skill, and can you verify that?",
        body: "Check the author, source repository, and publication date. A Skill from an unfamiliar author with no commit history, no public repository, or no community feedback deserves extra scrutiny, regardless of what the scan score says.",
      },
    ],
  },
  coverage: {
    heading: "What the scan covers",
    introduction:
      "SkillSecurity performs static analysis on the Skill content and any bundled files.",
    detectedLabel: "Detected categories include:",
    detected: [
      "prompt injection and instruction-override patterns;",
      "data exfiltration and credential access;",
      "dangerous shell commands and reverse-shell patterns;",
      "dependency and supply-chain risk signals;",
      "over-permissive tool configurations;",
      "obfuscated or hidden content.",
    ],
    notCoveredLabel: "Limitations:",
    notCovered: [
      "behavior that appears at runtime;",
      "heavily obfuscated or encrypted payloads;",
      "semantic intent in unfamiliar languages;",
      "vulnerabilities in the agent platform itself.",
    ],
  },
  report: {
    heading: "Understanding your scan report",
    summary:
      "Each report combines a Skill Safety Score and recommendation with findings, evidence, file locations, and scan coverage, plus options to export or share the result.",
    fixture: {
      ariaLabel: "Anonymous Skill security report",
      headerLabel: "Security report",
      identifier: "ANONYMOUS REVIEW",
      score: 63,
      scoreLabel: "Skill Safety Score",
      decisionTitle: "Review before installing",
      decisionBody: "Issues were found that could impact safety or lead to unintended behavior.",
      recommendationLabel: "Recommendation",
      recommendationTitle: "Manual review recommended",
      recommendationBody:
        "Inspect the finding evidence and the skipped file before trusting this Skill.",
      summary: [
        { label: "Source", value: "Uploaded workspace" },
        { label: "Scope", value: "7 scanned / 1 skipped" },
        { label: "Scan mode", value: "Static archive scan" },
        { label: "Finding count", value: "1 high" },
      ],
      coverageTitle: "Scan notes",
      coverageBody:
        "Seven files were scanned. One unsupported binary file was skipped and requires manual review.",
      findingsLabel: "Priority findings",
      findings: [
        {
          severity: "High",
          severityKey: "high",
          title: "Credential access followed by an outbound request",
          detail: "The bundled script reads an SSH private key before sending a network request.",
          evidence: 'readFile("~/.ssh/id_ed25519")',
          confidence: "High confidence",
          location: "scripts/review.ts:42",
        },
      ],
      exportAction: "Export JSON",
      exportHint: "Download the full report",
      shareAction: "Share report",
      shareHint: "Send an unlisted report card",
    },
  },
  faq: {
    heading: "Common questions",
    items: [
      {
        question: "Is it safe to install a Claude Code Skill after scanning?",
        answer:
          "Use the scan as one input to the decision. Review the findings and skipped files, then inspect the Skill's source, requested permissions, and outbound data flows before installing.",
      },
      {
        question: "Can a Skill bypass the scanner?",
        answer:
          "Yes. A Skill that uses novel obfuscation, encrypted payloads, or techniques outside the current rule pack may receive a higher Skill Safety Score than it deserves. Review the score alongside the findings, skipped files, and Skill source.",
      },
      {
        question: "Can SkillSecurity scan MCP servers?",
        answer:
          "SkillSecurity scans SKILL.md and bundled Skill files, not MCP servers. An included or referenced MCP configuration may appear in the report as a bundled configuration risk.",
      },
    ],
    action: "Scan a Skill",
  },
  sources: {
    anthropicSkills: "https://docs.anthropic.com/en/docs/claude-code/skills",
    snykToxicSkills: "https://snyk.io/blog/toxicskills-malicious-ai-agent-skills-clawhub/",
    csaSkillSupplyChain:
      "https://labs.cloudsecurityalliance.org/research/csa-research-note-skill-md-agent-context-poisoning-20260506/",
    owaspAgenticSkills: "https://owasp.org/www-project-agentic-skills-top-10/",
  },
} as const satisfies ClaudeSkillSecurityCopy;
